import { service } from '@ember/service';
import { set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { jwtDecode } from 'tio-common/utils/jwt-decode';
import SessionService from 'ember-simple-auth/services/session';
import { samlTokenExchangeRequest } from 'tio-data/requests/auth';
import type { Store } from '@warp-drive/core';
import type { RoleViewExtension as RoleView } from 'tio-data/data/resources/role-view.ext';
import type RouterService from '@ember/routing/router-service';
import { clearLimitSummaryCache } from 'tio-common/utils/tuition-assistance/limit-summary-cache';
import type LocaleRecommendationSnoozeService from 'tio-employee/services/locale-recommendation-snooze';
import { isSsoHandoffPath } from 'tio-employee/utils/sso-handoff';
import {
  clearRedirectTargetStamp,
  redirectTargetStampedAt,
  stampRedirectTarget,
} from 'tio-employee/utils/redirect-target-stamp';

interface AuthSessionPayload {
  iat: number;
  exp: number;
  id: number;
  employeeIds: number[];
  roles: RoleView[];
  impersonated_by?: number;
}

type Data = {
  transient?: Record<string, unknown>;
  permanent?: Record<string, unknown>;
  authenticated?: {
    access_token?: string;
    /**
     * Set by the database authenticator when authentication succeeded but the
     * user still has post-login requirements (MFA, terms acceptance, etc.) and
     * no real access token has been issued yet. When set, `handleAuthentication`
     * skips ESA's automatic transition so the login form / `post-login` route
     * can drive the next step.
     */
    noTransitionAfterAuth?: boolean;
  };
};

const warnBeforeExpirationMin = 3;

// Long enough for an IdP sign-in with MFA; older targets were not written by this sign-in.
const ssoRedirectTargetMaxAgeMs = 10 * 60 * 1000;

export default class AppSessionService extends SessionService<Data> {
  // NB: ESA's SessionService already exposes a readOnly `store` alias (the
  // session store, used below for `sessionDataUpdated`), so the WarpDrive data
  // store is injected under a distinct name to avoid shadowing it.
  @service('store') declare dataStore: Store;
  @service declare router: RouterService;
  @service declare cable: import('tio-common/services/cable').default;
  @service declare forethought: import('tio-employee/services/forethought').default;
  @service declare localeRecommendationSnooze: LocaleRecommendationSnoozeService;

  /**
   * A Unix timestamp representing the time at which the session will expire.
   */
  @tracked expireAt = 0;

  /**
   * Whether the session is expiring soon. This is set to `true` during the last few minutes of the
   * session, as defined by the internal constant `warnBeforeExpirationMin`.
   */
  @tracked isSessionExpiring = false;

  /**
   * Whether this session was created via admin impersonation.
   * When true, session renewal is blocked and the session expires after the
   * short TTL set by the API (currently 5 minutes).
   */
  @tracked isImpersonated = false;

  /**
   * The person ID of the admin who initiated the impersonation.
   * Only set when `isImpersonated` is true.
   */
  @tracked impersonatedByPersonId: number | null = null;

  /**
   * AuthKey#public_key the FE echoes back as the `x-auth-key` header so the
   * API can resolve which company / scope originated the request. Populated
   * by PreRegisterService from the `/preregister/:sub_domain` response. Lives
   * on the session so the every existing `@service declare session` injection
   * in adapters, authenticators, and routes can read it without a parallel
   * pre-register lookup.
   */
  @tracked publicKey?: string;

  /** Returns the named transient value, cleared when the session is invalidated. */
  getTransientValue(property: string) {
    return this.data.transient?.[property];
  }

  /** Stores a named value until session invalidation. */
  setTransientValue(property: string, value: unknown) {
    // Replace the whole object so the session service persists the updated value.

    set(this, 'data.transient', {
      ...this.data.transient,
      [property]: value,
    });
  }

  /** Returns the named value retained across session invalidation. */
  getPermanentValue(property: string) {
    return this.data.permanent?.[property];
  }

  /** Stores a named value across session invalidation. */
  setPermanentValue(property: string, value: unknown) {
    // Replace the whole object so the session service persists the updated value.

    set(this, 'data.permanent', {
      ...this.data.permanent,
      [property]: value,
    });
  }

  // Dedupes in-flight renewals so concurrent callers share one token_exchange.
  #renewPromise: Promise<void> | null = null;

  // Bound so we can detach in willDestroy.
  #boundVisibilityCheck = () => this.#reconcileExpirationState();

  #timers = new Map<'expire' | 'warn', ReturnType<typeof setTimeout>>();

  /**
   * Whether ESA's boot-time session restore has settled.
   *
   * `authenticators/saml` refuses a persisted restore during an SSO handoff, and
   * only the boot restore may be refused: ESA reuses `restore` for its cross-tab
   * `sessionDataUpdated` handler, where a rejection persists a cleared session
   * over the one another tab just created.
   */
  bootRestoreSettled = false;

  // Set by /logout before session.invalidate() to suppress handleInvalidation's
  // redundant transition. In-memory (not an ESA transient) so it doesn't
  // propagate via the storage event and suppress cross-tab logout elsewhere.
  isLoggingOut = false;

  /**
   * Whether the session ended without the user asking to log out — the token
   * expired, the API answered 401, or another tab invalidated it. `/logout`
   * reads this to tear down locally without sending the browser to the IdP's
   * single logout. In-memory, like `isLoggingOut`, so it stays with this tab.
   */
  skipIdpLogout = false;

  #ssoRedirectTarget: string | null = null;

  async setup() {
    try {
      await super.setup();
    } finally {
      this.bootRestoreSettled = true;
    }

    this.#configureSessionTimers();

    // Re-sync timers when another tab renews — ESA fires sessionDataUpdated
    // on cross-tab storage events. Without this, a stale timer here would
    // fire handleInvalidation at the old exp and log out every tab.
    // @ts-expect-error: store is a readOnly alias exposed by ESA SessionService
    this.store.on('sessionDataUpdated', () => {
      this.#configureSessionTimers();
      // Each tab's widget holds its own token snapshot — re-seed this one too.
      this.forethought.updateUserJwt(this.data.authenticated?.access_token);
    });

    // Singleton-scoped DOM listener; detached in willDestroy.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.#boundVisibilityCheck);
    }
  }

  willDestroy() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.#boundVisibilityCheck);
    }
    this.#timers.forEach((id) => clearTimeout(id));
    this.#timers.clear();
    super.willDestroy();
  }

  handleAuthentication(routeAfterAuthentication: string) {
    const noTransitionAfterAuth = this.data.authenticated?.noTransitionAfterAuth;

    if (!noTransitionAfterAuth) {
      // ESA would transitionTo a target mid-boot; auth-by-token redirects once the session has loaded.
      if (this.#isSsoHandoff()) {
        this.#ssoRedirectTarget = this.takeSsoRedirectTarget();
      } else {
        this.clearInvalidRedirectTarget();
      }

      if (!this.#ssoRedirectTarget) {
        super.handleAuthentication(routeAfterAuthentication);
      }
    }

    this.localeRecommendationSnooze.reset();
    this.skipIdpLogout = false;
    this.#configureSessionTimers();

    const accessToken = this.data.authenticated?.access_token;
    if (accessToken) {
      this.cable.updateToken(accessToken);
      this.forethought.updateUserJwt(accessToken);
    }
  }

  handleInvalidation() {
    // The identity boundary: drop cached limit summaries so a later login in
    // this tab never paints the previous session's data (the SPA transition
    // below does not reload the page, so module state survives).
    clearLimitSummaryCache();
    this.forethought.reset();

    if (this.isLoggingOut) {
      this.isLoggingOut = false;
      return;
    }
    this.skipIdpLogout = true;
    this.router.transitionTo('/logout');
  }

  async renew() {
    // Impersonated sessions expire naturally (5 min TTL); never renew.
    if (this.isImpersonated) {
      return;
    }

    if (this.#renewPromise) {
      return this.#renewPromise;
    }

    this.#renewPromise = this.#performRenewal();
    try {
      await this.#renewPromise;
    } finally {
      this.#renewPromise = null;
    }
  }

  async #performRenewal() {
    const { content: tokenInfo } = (await this.dataStore.request(samlTokenExchangeRequest())) as {
      content: { access_token: string };
    };

    await this.authenticate('authenticator:saml', tokenInfo);

    this.#configureSessionTimers();

    const refreshedToken = this.data.authenticated?.access_token;
    if (refreshedToken) {
      this.cable.updateToken(refreshedToken);
      this.forethought.updateUserJwt(refreshedToken);
    }
  }

  #isInvalidRedirect(route: string | null | undefined): boolean {
    if (!route) return true;
    // Compare only the path portion against the disallowed list so query/hash
    // variants are caught too (e.g. "/login?redirect=…", "/logout#done").
    // Captured targets come from `router.currentURL`, which carries query strings.
    const path = route.split(/[?#]/)[0] ?? route;
    const invalidPaths = ['/logged-out', '/logout', '/', '/login'];
    return invalidPaths.includes(path) || route.includes('undefined') || !route.startsWith('/');
  }

  /**
   * Drop a stored redirect target that ember-simple-auth would otherwise blindly
   * `transitionTo` after authentication. ESA's `handleSessionAuthenticated` does
   * `routerService.transitionTo(getRedirectTarget())` with no validation, so a
   * stale/invalid target (e.g. one that isn't a real "/"-prefixed path) is treated
   * as a route name. Fired mid-boot during the auth-by-token (SAML) flow, that
   * crashes the router's query-params path (`_queryParamsFor` on an empty
   * `routeInfos` — "Cannot read properties of undefined (reading 'name')"). With
   * the target cleared, ESA falls back to its safe `routeAfterAuthentication`.
   *
   * Exposed (not `#private`) so it can be unit-tested without ESA's full session
   * setup. The database-login path is unaffected — it sets `noTransitionAfterAuth`
   * and drives redirects through `post-login`'s own `validRedirectTarget` guard.
   */
  clearInvalidRedirectTarget() {
    const target = this.getRedirectTarget();
    if (target && this.#isInvalidRedirect(target)) {
      this.clearRedirectTarget();
    }
  }

  /**
   * Clears the stored target, returning it only when it is a valid path written within the window.
   *
   * @param now epoch milliseconds
   */
  takeSsoRedirectTarget(now = Date.now()) {
    const target = this.getRedirectTarget();
    const stampedAt = redirectTargetStampedAt();
    this.clearRedirectTarget();

    const isFresh =
      stampedAt !== null && stampedAt <= now && now - stampedAt <= ssoRedirectTargetMaxAgeMs;
    return target && isFresh && !this.#isInvalidRedirect(target) ? target : null;
  }

  /** The deep link held back from ESA during an SSO handoff; reading it clears it. */
  consumeSsoRedirectTarget() {
    const target = this.#ssoRedirectTarget;
    this.#ssoRedirectTarget = null;
    return target;
  }

  setRedirectTarget(url: string) {
    super.setRedirectTarget(url);
    stampRedirectTarget();
  }

  clearRedirectTarget() {
    super.clearRedirectTarget();
    clearRedirectTargetStamp();
  }

  #isSsoHandoff() {
    return isSsoHandoffPath();
  }

  #configureSessionTimers() {
    // @ts-expect-error: this is a dynamic property
    const { access_token } = this.session.get('authenticated');
    if (!access_token) {
      return;
    }
    const payload: AuthSessionPayload = jwtDecode(access_token);

    this.isImpersonated = !!payload.impersonated_by;
    this.impersonatedByPersonId = payload.impersonated_by ?? null;
    this.isSessionExpiring = false;
    this.expireAt = payload.exp * 1000;

    // `#setTimer` triggers immediately if the second parameter is in the past, so set `expire`
    // first, so we don't set `isSessionExpiring` if the session is already expired
    this.#setTimer('expire', this.expireAt, this.#handleExpire);
    this.#setTimer('warn', this.expireAt - warnBeforeExpirationMin * 60 * 1000, this.#handleWarn);
  }

  #handleExpire = () => {
    if (this.isAuthenticated) {
      const currentRoute = this.router.currentURL;
      if (currentRoute && !this.#isInvalidRedirect(currentRoute)) {
        this.setRedirectTarget(currentRoute);
      }
      this.handleInvalidation();
    }
  };

  #handleWarn = () => {
    if (this.isAuthenticated) {
      this.isSessionExpiring = true;
    }
  };

  // Backgrounded tabs throttle setTimeout, so a tab hidden across the
  // warn/expire boundary can surface with stale state. Re-check on visibility.
  #reconcileExpirationState() {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    if (!this.isAuthenticated || !this.expireAt) {
      return;
    }
    const now = Date.now();
    if (now >= this.expireAt) {
      this.#handleExpire();
    } else if (now >= this.expireAt - warnBeforeExpirationMin * 60 * 1000) {
      this.#handleWarn();
    }
  }

  #setTimer(type: 'expire' | 'warn', timestamp: number, callback: () => void) {
    const delay = timestamp - Date.now();
    const existing = this.#timers.get(type);
    if (existing) clearTimeout(existing);

    if (delay > 0) {
      // setTimeout delays are a SIGNED 32-bit int (~24.8 days max); anything
      // larger wraps and, when the residue lands negative, fires IMMEDIATELY —
      // instantly expiring a perfectly valid session. A far-future token expiry
      // (e.g. the demo-coverage audit's fabricated year-2100 token) hits this,
      // and whether it breaks depends on the CALENDAR (the wrap residue drifts
      // day by day). Cap each arm at the max and re-arm on fire until the real
      // timestamp is within reach.
      const MAX_TIMEOUT_DELAY_MS = 2 ** 31 - 1;
      if (delay > MAX_TIMEOUT_DELAY_MS) {
        this.#timers.set(
          type,
          setTimeout(() => this.#setTimer(type, timestamp, callback), MAX_TIMEOUT_DELAY_MS)
        );
      } else {
        this.#timers.set(type, setTimeout(callback, delay));
      }
    } else {
      this.#timers.delete(type);
      callback();
    }
  }
}
